import React from "react";
import { Box, Stack, Typography, Skeleton, Avatar } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import moment from "moment/moment";
import { getFirstSentence } from "../../utils/textUtils";
import { useCachedAvatar } from "../../hooks/useCachedAvatar";

/**
 * EditorPickCard
 * 에디터 픽 카드 - 글 제목만 표시하는 간단한 카드
 * @param {object} props
 * @param {object} props.thread - 스레드 메타 (id, text, permalink, timestamp, likes, replies 등)
 * @param {boolean} [props.isMobile]
 * @param {boolean} [props.isLoading] - 로딩 상태
 * @param {string} [props.userID] - 유저 ID (MagazineSectionsBlock에서 전달)
 * @param {string} [props.username] - 유저명 (MagazineSectionsBlock에서 전달)
 * @param {string} [props.name] - 유저 이름 (MagazineSectionsBlock에서 전달)
 * @param {string} [props.profileImage] - 프로필 이미지 URL (MagazineSectionsBlock에서 전달)
 * @param {boolean} [props.showUser=true] - 하단 유저 정보 표시 여부
 * @param {boolean} [props.showDate=false] - 날짜 표시 여부 (thread.timestamp 기반)
 */
const EditorPickCard = ({ thread, isMobile, isLoading = false, userID, username, name, profileImage, showUser = true, showDate = false }) => {
	const theme = useTheme();
	const navigate = useNavigate();

	// 하단 Writer 행에서 사용할 사용자 메타를 먼저 계산하고, 훅도 미리 호출해 훅 순서를 보장
	const finalUserID = userID || thread?.userId || thread?.userID || thread?.user?.id || null;
	const finalUsername = username || thread?.username || thread?.user?.username || "";
	const { imageSrc } = useCachedAvatar(String(finalUserID || ''), profileImage, 160);

	// 로딩 상태이거나 데이터가 없을 때 스켈레톤 표시
	if (isLoading || !thread || !thread.text) {
		return (
			<Stack
				spacing={isMobile ? 2 : 2}
				sx={{
					width: "100%",
					height: "100%",
					px: isMobile ? 2 : 4,
					py: isMobile ? 3 : 5,
					justifyContent: "space-between",
				}}
			>
				<Box sx={{ minWidth: 0 }}>
					{/* 제목 스켈레톤 */}
					<Skeleton
						variant="text"
						width={isMobile ? "90%" : "85%"}
						height={isMobile ? 28 : 32}
						sx={{ mb: 1 }}
					/>
					<Skeleton
						variant="text"
						width={isMobile ? "70%" : "60%"}
						height={isMobile ? 28 : 32}
						sx={{ mb: isMobile ? 1.5 : 2 }}
					/>
					{/* 본문 스켈레톤 */}
					<Skeleton
						variant="text"
						width="95%"
						height={isMobile ? 20 : 24}
						sx={{ mb: 0.5 }}
					/>
					<Skeleton
						variant="text"
						width="80%"
						height={isMobile ? 20 : 24}
					/>
				</Box>
				{/* 작가 정보 스켈레톤 */}
				{showUser && (
					<Stack direction="row" alignItems="center" spacing={1}>
						<Skeleton
							variant="circular"
							width={isMobile ? 32 : 44}
							height={isMobile ? 32 : 44}
						/>
						<Box sx={{ minWidth: 0, flex: 1 }}>
							<Skeleton
								variant="text"
								width="60%"
								height={isMobile ? 16 : 20}
								sx={{ mb: 0.5 }}
							/>
							<Skeleton
								variant="text"
								width="40%"
								height={isMobile ? 14 : 16}
							/>
						</Box>
					</Stack>
				)}
			</Stack>
		);
	}

	const title = getFirstSentence(thread?.text || "") || `Thread ${thread?.id}`;
	const body = (thread?.text || "").slice(title.length).trim();

	// props 유저 메타는 위에서 계산 및 아바타 프리로드 완료 (훅 순서 보장)

	return (
		<Stack
			spacing={isMobile ? 2 : 2}
			sx={{
				width: "100%",
				height: "100%",
				px: isMobile ? 2 : 4,
				py: isMobile ? 3 : 5,
				justifyContent: "space-between",
				transition: "background-color 0.2s ease-in-out, color 0.2s ease-in-out",
				cursor: "pointer",
				"&:hover": {
					backgroundColor: theme.palette.primary.main,
					"& *": {
						color: theme.palette.background.paper + " !important",
					},
				},
			}}
			onClick={() => {
				// 원글 링크가 있으면 새 탭에서 열기
				if (thread?.permalink) {
					window.open(thread.permalink, '_blank', 'noopener,noreferrer');
					return;
				}
				// 없으면 컬렉션 페이지로 이동
				const colId = thread?.collectionId;
				if (colId && thread?.id) {
					navigate(`/collection/${colId}?threadID=${thread.id}`);
				}
			}}
		>
			<Box sx={{ minWidth: 0 }}>
				<Box sx={{ textDecoration: "none" }}>
					{showDate && thread?.timestamp && (
						<Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 500,
									lineHeight: 1,
									color: theme.palette.text.secondary,
								}}
							>
								{moment(thread.timestamp).format("MM/DD")}
							</Typography>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 500,
									lineHeight: 1,
									color: theme.palette.text.secondary,
								}}
							>
								{moment(thread.timestamp).format("ddd")}
							</Typography>
						</Stack>
					)}
					<Typography
						variant={isMobile ? "h6" : "h5"}
						sx={{
							fontFamily: '"RIDIBatang", "Noto Serif KR", serif',
							fontWeight: 700,
							color: theme.palette.text.primary,
							lineHeight: 1.4,
							height: isMobile ? "auto" : "56px",
							overflow: "hidden",
							wordBreak: "keep-all",
							display: "-webkit-box",
							WebkitLineClamp: isMobile ? 2 : 3,
							WebkitBoxOrient: "vertical",
							fontSize: isMobile ? "1rem" : undefined,
							mb: isMobile ? 1.5 : 2,
						}}
					>
						{title.replace(/�/g, "")}
					</Typography>
					<Typography
						variant={isMobile ? "body2" : "body2"}
						sx={{
							color: body ? theme.palette.text.primary : "transparent",
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
							fontSize: isMobile ? "0.8rem" : undefined,
							lineHeight: 1.5,
							height: isMobile ? "2.4rem" : "3.2rem",
							minHeight: isMobile ? "2.4rem" : "3.2rem",
						}}
					>
						{body ? body.replace(/�/g, "") : "\u00A0"}
					</Typography>
				</Box>
			</Box>
			{showUser && (
				<Box sx={{ minWidth: 0, width: '100%', overflow: 'hidden' }}>
					{/* MagazineCollectionCard와 동일한 하단 Writer 행 */}
					<Stack
						direction="row"
						spacing={isMobile ? 1 : 1.5}
						alignItems="center"
						sx={{ minWidth: 0 }}
					>
						<Avatar
							src={imageSrc || undefined}
							sx={{ width: isMobile ? 32 : 44, height: isMobile ? 32 : 44 }}
						>
							{finalUsername ? finalUsername.charAt(0).toUpperCase() : "?"}
						</Avatar>
						<Typography
							variant={isMobile ? "caption" : "body1"}
							sx={{
								fontWeight: 700,
								fontSize: isMobile ? "0.75rem" : undefined,
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								maxWidth: "100%",
							}}
						>
							@{finalUsername}
						</Typography>
					</Stack>
				</Box>
			)}
		</Stack>
	);
};

export default EditorPickCard;
